import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Eye, Upload, Check, X, AlertCircle, ExternalLink, Download, RefreshCw, ArrowLeft, FileText, User, Calendar, Building } from 'lucide-react';
import { checkListData } from '../../data/checklist';
import PDFMergeUploader from '../../components/PDFMergeUploader';

// Modal component for viewing documents (reused from original)
const ViewDocumentModal = ({ 
    isOpen, 
    onClose, 
    documentUrl,
    docMetadata,
    handleRemoveDocument
  }) => {
    const [iframeError, setIframeError] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
  
    // Reset error state when modal opens or URL changes
    useEffect(() => {
      if (isOpen) {
        setIframeError(false);
        setIsLoading(true);
      }
    }, [isOpen, documentUrl]);
  
    // Handle iframe load error
    const handleIframeError = () => {
      setIframeError(true);
      setIsLoading(false);
    };
  
    // Handle iframe load success
    const handleIframeLoad = () => {
      setIsLoading(false);
    };
  
    // Get file extension to determine document type
    const getFileExtension = (url) => {
      try {
        const pathname = new URL(url).pathname;
        return pathname.split('.').pop()?.toLowerCase() || '';
      } catch {
        return '';
      }
    };
  
    // Check if URL is likely to work in iframe
    const isEmbeddableUrl = (url) => {
      // Google Drive files with proper preview URLs should work
      if (url.includes('drive.google.com/file/d/') && url.includes('/preview')) {
        return true;
      }
      
      // Google Docs, Sheets, Slides with embed parameter
      if ((url.includes('docs.google.com') || url.includes('sheets.google.com') || url.includes('slides.google.com')) 
          && url.includes('/edit')) {
        return true;
      }
      
      const fileExt = getFileExtension(url);
      const embeddableExtensions = ['pdf', 'txt', 'csv', 'xml', 'json'];
      
      // Direct file URLs
      if (embeddableExtensions.includes(fileExt)) {
        return true;
      }
      
      return false;
    };
  
    // Convert Google Drive URLs to embeddable format
    const getEmbeddableUrl = (url) => {
      // Handle Google Drive URLs
      if (url.includes('drive.google.com')) {
        return convertGoogleDriveUrl(url);
      }
      
      // For other URLs, try Google Docs viewer
      const fileExt = getFileExtension(url);
      const viewableExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
      
      if (viewableExtensions.includes(fileExt)) {
        return `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
      }
      return url;
    };
  
    // Convert Google Drive sharing URL to embeddable format
    const convertGoogleDriveUrl = (url) => {
      try {
        // Extract file ID from various Google Drive URL formats
        let fileId = '';
        
        // Format: https://drive.google.com/file/d/FILE_ID/view
        if (url.includes('/file/d/')) {
          fileId = url.split('/file/d/')[1].split('/')[0];
        }
        // Format: https://drive.google.com/open?id=FILE_ID
        else if (url.includes('open?id=')) {
          fileId = url.split('open?id=')[1].split('&')[0];
        }
        // Format: https://docs.google.com/document/d/FILE_ID/
        else if (url.includes('/document/d/')) {
          fileId = url.split('/document/d/')[1].split('/')[0];
        }
        // Format: https://docs.google.com/spreadsheets/d/FILE_ID/
        else if (url.includes('/spreadsheets/d/')) {
          fileId = url.split('/spreadsheets/d/')[1].split('/')[0];
        }
        // Format: https://docs.google.com/presentation/d/FILE_ID/
        else if (url.includes('/presentation/d/')) {
          fileId = url.split('/presentation/d/')[1].split('/')[0];
        }

        if (fileId) {
          // Return the embeddable preview URL
          return `https://drive.google.com/file/d/${fileId}/preview`;
        }
      } catch (error) {
        console.error('Error converting Google Drive URL:', error);
      }
      
      return url;
    };
  
    // Open document in new tab
    const openInNewTab = () => {
      window.open(documentUrl, '_blank', 'noopener,noreferrer');
    };
  
    // Download document
    const downloadDocument = () => {
      const link = document.createElement('a');
      link.href = documentUrl;
      link.download = documentUrl.split('/').pop() || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const handleDeleteDocument = async () => {
      // remove document from employeeData
      try {
        const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/emp-documnets/employee-documents`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
          body: JSON.stringify({
            employee_key: docMetadata.employee_key,
            [docMetadata.docType]: false
          })
        });
        if (!response.ok) {
          throw new Error('Failed to delete document');
        }
        handleRemoveDocument(docMetadata.docType);
        onClose();
        console.log('Document deleted successfully');
      } catch (error) {
        console.error('Error deleting document:', error);
      }
    }
  
    const renderContent = () => {
      if (iframeError || !isEmbeddableUrl(documentUrl)) {
        return (
          <div className="flex flex-col items-center justify-center h-[70vh] text-center p-8">
            <AlertCircle className="h-16 w-16 text-yellow-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Document Cannot Be Displayed
            </h3>
            <p className="text-gray-600 mb-6 max-w-md">
              This document cannot be embedded due to security restrictions. 
              You can still view or download it using the options below.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={openInNewTab}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                Open in New Tab
              </button>
              <button
                onClick={downloadDocument}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <Download className="h-4 w-4" />
                Download
              </button>
            </div>
          </div>
        );
      }
  
      return (
        <div className="relative">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}
          <iframe
            src={getEmbeddableUrl(documentUrl)}
            className="w-full h-[70vh] border rounded"
            title="Document Viewer"
            allowFullScreen
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
          />
        </div>
      );
    };
  
    if (!isOpen) return null;
  
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
          <div className="flex justify-between items-center p-4 border-b">
            <h2 className="text-xl font-semibold">Document Viewer</h2>
            <div className="flex items-center gap-2">
              {!iframeError && (
                <>
                  <button
                    onClick={openInNewTab}
                    className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100"
                    title="Open in new tab"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </button>
                  <button
                    onClick={downloadDocument}
                    className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100"
                    title="Download"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                </>
              )}
              <button 
                onClick={onClose} 
                className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          
          <div className="flex-1 p-4 overflow-auto">
            {renderContent()}
          </div>
          
          <div className="p-4 border-t flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {iframeError ? 'External viewing options available' : 'Document loaded successfully'}
            </div>
            <div>
            <button
              onClick={handleDeleteDocument}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              Delete Document
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Close
            </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

// Modal component for uploading documents
const UploadDocumentModal = ({ 
  isOpen, 
  onClose, 
  onUpload,
  employeeId,
  documentType 
}) => {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (file) => {
    if (file) {
      setFile(file);
      handleSubmit(file);
    }
  };

  const handleSubmit = async (file) => {
    if (!file) return;
    
    setIsUploading(true);
    try {
      await onUpload(file);
      onClose();
      setFile(null);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">Upload Document</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-6 w-6" />
          </button>
        </div>
        <form className="p-6">
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              Document Type: <span className="font-medium">
                {documentType.split('_').map(word => 
                  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                ).join(' ')}
              </span>
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File
            </label>
            <PDFMergeUploader onUpload={handleFileChange}/>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isUploading}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Document card component
const DocumentCard = ({ docType, docValue, onView, onUpload }) => {
  const hasDocument = Boolean(docValue);
  const documentLink = docValue;
  
  const formatDocumentName = (name) => {
    return name.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  return (
    <div className={`border rounded-lg p-4 transition-all duration-200 ${
      hasDocument 
        ? 'border-green-200 bg-green-50 hover:shadow-md' 
        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
    }`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${
            hasDocument ? 'bg-green-100' : 'bg-gray-100'
          }`}>
            <FileText className={`h-5 w-5 ${
              hasDocument ? 'text-green-600' : 'text-gray-400'
            }`} />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">
              {formatDocumentName(docType)}
            </h3>
            <p className={`text-sm ${
              hasDocument ? 'text-green-600' : 'text-gray-500'
            }`}>
              {hasDocument ? 'Available' : 'Not Available'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {hasDocument ? (
            <>
              <div className="flex items-center gap-1 text-green-600">
                <Check className="h-4 w-4" />
                <span className="text-xs font-medium">Uploaded</span>
              </div>
              <button
                onClick={() => documentLink && onView(documentLink)}
                className="p-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                disabled={!documentLink}
                title="View document"
              >
                <Eye className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-1 text-gray-500">
                <X className="h-4 w-4" />
                <span className="text-xs font-medium">Missing</span>
              </div>
              <button
                onClick={() => onUpload(docType)}
                className="p-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                title="Upload document"
              >
                <Upload className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const EmployeeDocumentAccess = () => {
  const { employeeCode } = useParams();
  const [employeeData, setEmployeeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [viewModal, setViewModal] = useState({ isOpen: false, url: '', docMetadata: null });
  const [uploadModal, setUploadModal] = useState({ 
    isOpen: false, 
    documentType: '' 
  });
  // Add filter state
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Fetch employee data
  const fetchEmployeeData = async () => {
    setIsLoading(true);
    try {
      // Simulate API call
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/emp-documnets/get-document/${employeeCode}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });
      const {employee_key, ...rest} = await response.json();
      setEmployeeData({...rest, eid: employee_key.split('_')[0], employee_name: employee_key.split('_').slice(1).join(' ')});
    } catch (error) {
      console.error('Error fetching employee data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // const handleUploadDocument = async (file) => {
  //   try {
  //     const formData = new FormData();
  //     formData.append("employeeId", uploadModal.employeeId);
  //     formData.append("documentType", uploadModal.documentType);
  //     formData.append("file", file);

  //     const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/upload-document`, {
  //       method: "POST",
  //       headers: {
  //         Authorization: `Bearer ${localStorage.getItem("access_token")}`,
  //       },
  //       body: formData,
  //     });

  //     if (!response.ok) {
  //       throw new Error("Upload failed");
  //     }

  //     const result = await response.json();

  //     setDocuments((prevDocs) =>
  //       prevDocs.map((doc) => {
  //         if (doc.eid === uploadModal.employeeId) {
  //           return {
  //             ...doc,
  //             [uploadModal.documentType]: result.fileUrl || URL.createObjectURL(file), 
  //           };
  //         }
  //         return doc;
  //       })
  //     );
  //   } catch (error) {
  //     console.error("Error uploading file:", error);
  //   }
  // };


  useEffect(() => {
    if (employeeCode) {
      fetchEmployeeData();
    }
  }, [employeeCode]);

  // Handle document view
  const handleViewDocument = (url, docMetadata) => {
    setViewModal({ isOpen: true, url, docMetadata });
  };

  // Handle document upload
  const handleUploadClick = (documentType) => {
    setUploadModal({ isOpen: true, documentType });
  };

  const handleUploadDocument = async (file) => {
    console.log('Uploading file:', {
      employeeCode,
      documentType: uploadModal.documentType,
      filename: file.name,
      size: file.size,
      type: file.type
    });
    
    // Simulate API call
    try {
      const formData = new FormData();
      formData.append("employeeId", employeeCode);
      formData.append("documentType", uploadModal.documentType);
      formData.append("file", file);
      const response = await fetch(`${process.env.REACT_APP_PUBLIC_API_BASE_URL}/v1/employees/upload-document`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
        body: formData,
      });
      if (!response.ok) {
        throw new Error("Upload failed");
      }
      const result = await response.json();
      console.log("Upload result:", result);
    } catch (error) {
      console.error("Error uploading file:", error);
    }
    
    // Update the local state to reflect the upload
    setEmployeeData(prevData => ({
      ...prevData,
      [uploadModal.documentType]: URL.createObjectURL(file)
    }));
  };

  // Handle filter selection
  const handleFilterSelect = (filterType) => {
    setSelectedFilter(filterType);
  };

  // Get document statistics
  const getDocumentStats = () => {
    if (!employeeData) return { total: 0, uploaded: 0, missing: 0 };
    
    const documentKeys = Object.keys(employeeData).filter(key => 
      !['eid', 'employee_name', 'department', 'joinDate'].includes(key)
    );
    
    const uploaded = documentKeys.filter(key => {
      const docValue = employeeData[key];
      return typeof docValue === 'object' ? docValue.status : Boolean(docValue);
    }).length;
    
    return {
      total: documentKeys.length,
      uploaded,
      missing: documentKeys.length - uploaded
    };
  };

  const documentStats = getDocumentStats();


  // Filter documents based on selected filter
  const getFilteredDocuments = () => {
    if (!employeeData) return [];
    
    const documentKeys = Object.keys(employeeData).filter(key => 
      !['eid', 'employee_name', 'department', 'joinDate'].includes(key)
    );

    switch (selectedFilter) {
      case 'uploaded':
        return documentKeys.filter(key => {
          const docValue = employeeData[key];
          return typeof docValue === 'object' ? docValue.status : Boolean(docValue);
        });
      case 'missing':
        return documentKeys.filter(key => {
          const docValue = employeeData[key];
          return !(typeof docValue === 'object' ? docValue.status : Boolean(docValue));
        });
      default:
        return documentKeys;
    }
  };

  const handleRemoveDocument = (docType) => {
    setEmployeeData(prevData => ({
      ...prevData,
      [docType]: false
    }));
  }

  if (isLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading employee documents...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!employeeData) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Employee Not Found</h2>
          <p className="text-gray-600">No employee data found for code: {employeeCode}</p>
        </div>
      </div>
    );
  }

  const filteredDocuments = getFilteredDocuments();

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* View Document Modal */}
      <ViewDocumentModal
        isOpen={viewModal.isOpen}
        onClose={() => setViewModal({ isOpen: false, url: '' })}
        documentUrl={viewModal.url}
        docMetadata={viewModal.docMetadata}
        handleRemoveDocument={handleRemoveDocument}
      />

      {/* Upload Document Modal */}
      <UploadDocumentModal
        isOpen={uploadModal.isOpen}
        onClose={() => setUploadModal({ isOpen: false, documentType: '' })}
        onUpload={handleUploadDocument}
        employeeId={employeeData.eid}
        documentType={uploadModal.documentType}
      />

 {/* Header */}
<div className="mb-8">
  <div className="flex items-center gap-3 mb-6">
    <button 
      onClick={() => window.history.back()}
      className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
      title="Go back"
    >
      <ArrowLeft className="h-5 w-5" />
    </button>
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Employee Documents</h1>
      <p className="text-sm sm:text-base text-gray-600">View and manage document status</p>
    </div>
  </div>

  {/* Employee Info Card */}
  <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6 mb-6">
    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
      
      {/* Avatar + Info */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        <div className="p-3 bg-blue-100 rounded-full self-start sm:self-auto">
          <User className="h-8 w-8 text-blue-600" />
        </div>
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">{employeeData.employee_name}</h2>
          
          {/* Employee meta info */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 mt-2 text-gray-600">
            <div className="flex items-center gap-1">
              <FileText className="h-4 w-4" />
              <span className="text-sm sm:text-base">ID: {employeeData.eid}</span>
            </div>
            {employeeData.department && (
              <div className="flex items-center gap-1">
                <Building className="h-4 w-4" />
                <span className="text-sm sm:text-base">{employeeData.department}</span>
              </div>
            )}
            {employeeData.joinDate && (
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span className="text-sm sm:text-base">Joined: {employeeData.joinDate}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Refresh button */}
      <button
        onClick={fetchEmployeeData}
        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors self-start sm:self-auto"
        title="Refresh data"
      >
        <RefreshCw className="h-5 w-5" />
      </button>
    </div>
  </div>

        {/* Statistics Cards - Now clickable with highlighting */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div 
            onClick={() => handleFilterSelect('all')}
            className={`rounded-lg p-4 cursor-pointer transition-all duration-200 transform hover:scale-105 hover:shadow-lg ${
              selectedFilter === 'all' 
                ? 'bg-blue-100 border-2 border-blue-400 ring-2 ring-blue-200' 
                : 'bg-blue-50 border border-blue-200 hover:bg-blue-100'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${
                  selectedFilter === 'all' ? 'text-blue-700' : 'text-blue-600'
                }`}>Total Documents</p>
                <p className={`text-2xl font-bold ${
                  selectedFilter === 'all' ? 'text-blue-800' : 'text-blue-700'
                }`}>{documentStats.total}</p>
              </div>
              <FileText className={`h-8 w-8 ${
                selectedFilter === 'all' ? 'text-blue-600' : 'text-blue-500'
              }`} />
            </div>
            {selectedFilter === 'all' && (
              <div className="mt-2 text-xs text-blue-700 font-medium">
                ✓ Selected - Showing all documents
              </div>
            )}
          </div>
          
          <div 
            onClick={() => handleFilterSelect('uploaded')}
            className={`rounded-lg p-4 cursor-pointer transition-all duration-200 transform hover:scale-105 hover:shadow-lg ${
              selectedFilter === 'uploaded' 
                ? 'bg-green-100 border-2 border-green-400 ring-2 ring-green-200' 
                : 'bg-green-50 border border-green-200 hover:bg-green-100'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${
                  selectedFilter === 'uploaded' ? 'text-green-700' : 'text-green-600'
                }`}>Uploaded</p>
                <p className={`text-2xl font-bold ${
                  selectedFilter === 'uploaded' ? 'text-green-800' : 'text-green-700'
                }`}>{documentStats.uploaded}</p>
              </div>
              <Check className={`h-8 w-8 ${
                selectedFilter === 'uploaded' ? 'text-green-600' : 'text-green-500'
              }`} />
            </div>
            {selectedFilter === 'uploaded' && (
              <div className="mt-2 text-xs text-green-700 font-medium">
                ✓ Selected - Showing uploaded documents
              </div>
            )}
          </div>
          
          <div 
            onClick={() => handleFilterSelect('missing')}
            className={`rounded-lg p-4 cursor-pointer transition-all duration-200 transform hover:scale-105 hover:shadow-lg ${
              selectedFilter === 'missing' 
                ? 'bg-red-100 border-2 border-red-400 ring-2 ring-red-200' 
                : 'bg-red-50 border border-red-200 hover:bg-red-100'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${
                  selectedFilter === 'missing' ? 'text-red-700' : 'text-red-600'
                }`}>Missing</p>
                <p className={`text-2xl font-bold ${
                  selectedFilter === 'missing' ? 'text-red-800' : 'text-red-700'
                }`}>{documentStats.missing}</p>
              </div>
              <AlertCircle className={`h-8 w-8 ${
                selectedFilter === 'missing' ? 'text-red-600' : 'text-red-500'
              }`} />
            </div>
            {selectedFilter === 'missing' && (
              <div className="mt-2 text-xs text-red-700 font-medium">
                ✓ Selected - Showing missing documents
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Documents Grid */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">
            Document Status
            {selectedFilter !== 'all' && (
              <span className="ml-2 text-sm font-normal text-gray-600">
                - Showing {selectedFilter} documents ({filteredDocuments.length})
              </span>
            )}
          </h3>
          <div className="text-sm text-gray-600">
            {documentStats.uploaded} of {documentStats.total} documents uploaded
          </div>
        </div>
        
        {filteredDocuments.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              {selectedFilter === 'uploaded' && <Check className="h-16 w-16 mx-auto" />}
              {selectedFilter === 'missing' && <AlertCircle className="h-16 w-16 mx-auto" />}
              {selectedFilter === 'all' && <FileText className="h-16 w-16 mx-auto" />}
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No {selectedFilter === 'all' ? '' : selectedFilter} documents found
            </h3>
            <p className="text-gray-600">
              {selectedFilter === 'uploaded' && 'No documents have been uploaded yet.'}
              {selectedFilter === 'missing' && 'All documents have been uploaded!'}
              {selectedFilter === 'all' && 'No documents are configured for this employee.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDocuments.map((docType) => (
              <DocumentCard
                key={docType}
                docType={docType}
                docValue={employeeData[docType]}
                onView={(url) => handleViewDocument(url, { employee_key: `${employeeData.eid}_${employeeData.employee_name.split(' ').join('_')}`, docType })}
                onUpload={handleUploadClick}
              />
            ))}
          </div>
        )}
        
        {/* Progress Bar */}
        <div className="mt-6 pt-6 border-t">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Document Completion</span>
            <span className="text-sm font-medium text-gray-700">
              {Math.round((documentStats.uploaded / documentStats.total) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(documentStats.uploaded / documentStats.total) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDocumentAccess;